import React, { useEffect } from 'react';
import Button from '../ui/Button';
import FormInput from '../ui/FormInput';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { loginUser } from '../../services/api/implementation/impl';
import Cookies from 'js-cookie';
import socket from '../../../../socket';
import mixpanel from 'mixpanel-browser';
import { Phone } from 'lucide-react';
import HousinGramLogo from '../../../shared/assets/svg/Logo.svg';
import LoginFormFooter from './LoginFormFooter';
import { handlePostLoginStorage, saveAuthSessionCookies } from '../../utils/authStorage';
import { operationsLoginPath } from '../../utils/workforceAccess';

const RegisterForm = () => {
  /* 
    Hooks
  */
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue
  } = useForm();

  /* 
    Event handlers
  */
  const { mutate: handleLogin } = useMutation({
    mutationFn: async (payload) => await loginUser(payload),
    onSuccess: async (res) => {
      console.log({ res });
      const challengeName = res?.data?.data?.challengeName;
      const challengeSession = res?.data?.data?.session;
      const challengeMessage = res?.data?.message;
      const challengeEmail = res?.data?.data?.email;

      if (
        challengeName === 'NEW_PASSWORD_REQUIRED' &&
        challengeSession &&
        challengeMessage === 'Additional authentication challenge required'
      ) {
        const userEmail = (challengeEmail || document.querySelector('input[name="email"]')?.value || '')
          .trim();
        navigate(
          `/auth/complete-new-password?email=${encodeURIComponent(userEmail)}&session=${encodeURIComponent(
            challengeSession
          )}`
        );
        toast.success('Set a new password to continue.');
        return;
      }

      const newToken = res.data.data.token;
      const newUserId = res?.data?.data?.user?.id;

      handlePostLoginStorage(newUserId);
      saveAuthSessionCookies(res.data.data);
      queryClient.clear();

      // Navigate by role
      const userRole = res?.data?.data?.user?.role?.name;
      const userVertical = res?.data?.data?.user?.vertical;
      const userDesignation = res?.data?.data?.user?.designation?.name;
      mixpanel.identify(res?.data?.data?.user?.id); // Use a unique user ID
      mixpanel.people.set({
        $name: res?.data?.data?.user?.name,
        $phone: res?.data?.data?.user?.phone_number,
        $email: res?.data?.data?.user?.email,
        $vertical: res?.data?.data?.user?.vertical,
        $role: res?.data?.data?.user?.role?.name,
        $designation: res?.data?.data?.user?.designation?.name
      });

      // // Optional: Track the login event
      mixpanel.track('User Logged In', {
        'Login Method': 'Email'
      });

      // Trigger custom login event for projects context
      window.dispatchEvent(
        new CustomEvent('userLoggedIn', {
          detail: {
            userId: res?.data?.data?.user?.id,
            userRole: res?.data?.data?.user?.role?.name,
            timestamp: new Date().toISOString()
          }
        })
      );

      const userDesignationLevel = res?.data?.data?.user?.level;

      if (userRole === 'builder') {
        if (userVertical === 'Sales') {
          // if (userDesignationLevel === 'Low') {
          //   // navigate('/builder/sales');
          //   toast.error('You are not authorized to access this page');
          //   Cookies.remove('token');
          //   Cookies.remove('userRoleId');
          //   Cookies.remove('userId');
          //   Cookies.remove('userDesignation');
          //   Cookies.remove('userRole');
          //   Cookies.remove('userVertical');
          //   Cookies.remove('userDesignationLevel');
          //   return;
          // } else if (userDesignationLevel === 'Medium' || userDesignationLevel === 'High') {
          navigate('/builder/sales');
          // }
        } else if (userVertical === 'Operations') {
          // Supervisor lands on the labour register (daily muster). Site
          // Engineer and other Ops titles land on the dashboard (plans,
          // quality, wage bills).
          navigate(operationsLoginPath(userDesignation));
        } else if (userVertical === 'Accounts') {
          navigate('/builder/reports');
        } else if (userVertical === 'HR') {
          // The dashboard is a management overview HR has no part of; the staff
          // directory is where their day starts.
          navigate('/builder/hr/employees');
        } else if (userVertical === 'Legal') {
          navigate('/builder/legal');
        } else if (userVertical === 'Inventory') {
          if (userDesignationLevel === 'Low') {
            toast.error('You are not authorized to access this page');
            Cookies.remove('token');
            Cookies.remove('userRoleId');
            Cookies.remove('userId');
            Cookies.remove('userDesignation');
            Cookies.remove('userRole');
            Cookies.remove('userVertical');
            Cookies.remove('userDesignationLevel');
            Cookies.remove('domino_enabled');
            return;
          } else if (userDesignationLevel === 'Medium' || userDesignationLevel === 'High') {
            navigate('/builder/dashboard');
          }
        } else {
          navigate('/builder/dashboard');
        }
      } else if (userRole === 'admin') {
        navigate('/super-admin/home');
      }

      // Connect with the fresh token (socket auth reads the cookie)
      socket.connect();
      toast.success('User successfully logged in');
    },

    onError: (err) => {
      console.error('Error logging in user:', err);
      toast.error(err?.response?.data?.message || 'Incorrect email or password');
    }
  });

  /* 
    Handle autofill synchronization and keyboard events
    (setTimeout ensures the browser has time to fill them)
  */
  useEffect(() => {
    setTimeout(() => {
      const emailInput = document.querySelector('input[name="email"]');
      const passwordInput = document.querySelector('input[name="password"]');
      if (emailInput && passwordInput) {
        setValue('email', emailInput.value);
        setValue('password', passwordInput.value);

        // Add event listeners directly to the input elements
        emailInput.addEventListener('keydown', handleEmailKeyDown);
        passwordInput.addEventListener('keydown', handlePasswordKeyDown);

        // Cleanup function
        return () => {
          emailInput.removeEventListener('keydown', handleEmailKeyDown);
          passwordInput.removeEventListener('keydown', handlePasswordKeyDown);
        };
      }
    }, 200);
  }, [setValue]);

  /* 
    Submit handler
  */
  const handleFormSubmit = (data) => {
    // Read directly from DOM to ensure we get autofilled values
    const emailInput = document.querySelector('input[name="email"]');
    const passwordInput = document.querySelector('input[name="password"]');

    const trimmedData = {
      email: (emailInput?.value || data.email)?.trim() || '',
      password: (passwordInput?.value || data.password)?.trim() || ''
    };
    handleLogin(trimmedData);
  };

  /* 
    Handle email field key down
  */
  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const passwordInput = document.querySelector('input[name="password"]');
      if (passwordInput) {
        passwordInput.focus();
      }
    }
  };

  /* 
    Handle password field key down
  */
  const handlePasswordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Get values directly from DOM inputs to ensure we have the latest values
      const emailInput = document.querySelector('input[name="email"]');
      const passwordInput = document.querySelector('input[name="password"]');

      const trimmedData = {
        email: emailInput?.value?.trim() || '',
        password: passwordInput?.value?.trim() || ''
      };

      handleLogin(trimmedData);
    }
  };

  return (
    <form
      className="mx-auto flex min-h-full w-full max-w-md flex-col"
      onSubmit={handleSubmit(handleFormSubmit)}
      autoComplete="on">
      <div className="flex flex-col items-center gap-1 pt-2">
        <img src={HousinGramLogo} alt="HousinGram" className="h-11 w-auto" />
        <p className="text-center text-xs text-gray-500">
          Complete Real Estate Business Management Platform
        </p>
      </div>

      <div className="flex flex-1 flex-col justify-center gap-6 py-8">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-2xl font-semibold text-stone-800">Welcome back to HousinGram!</h1>
          <p className="text-sm text-gray-500">
            Sign in to manage your projects, teams, sales and operations.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <FormInput
            width={100}
            register={register}
            register_key="email"
            label="Email"
            type="email"
            isRequired
            autoComplete="username"
          />

          <FormInput
            width={100}
            register={register}
            register_key="password"
            label="Password"
            type="password"
            isRequired
            autoComplete="current-password"
          />

          <div className="flex w-full items-center justify-between text-sm text-stone-700">
            <span>Trouble logging in ?</span>
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => navigate('/auth/forgot-password')}>
              Forgot password
            </button>
          </div>
        </div>

        <Button type="submit" displayType="full-w-primary" width={100}>
          Login
        </Button>

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-sm text-gray-400">or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/auth/login-phone')}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-customBlue transition-colors hover:bg-gray-50">
          <Phone className="h-4 w-4" />
          Login via Phone
        </button>
      </div>

      <LoginFormFooter />
    </form>
  );
};

export default RegisterForm;
